import { computed, Injectable, signal } from '@angular/core';

/**
 * Shared state for the app shell's left sidebar.
 *
 * Two modes, chosen by viewport width:
 *  - md and up   → the sidebar takes space in the layout (icon rail when closed)
 *  - below md    → it overlaps the viewport as a drawer with a dimming underlay
 *
 * The md breakpoint (768px) is the JS half of a pair: it must stay in step with
 * `$sizebar-minimized-width-minimum-screen-width-breakpoint` in
 * `libs/xplat/web/scss/src/layout/sidebar.variables.scss`, which gates the
 * matching CSS. Change one and you must change the other.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  /** Matches the SCSS breakpoint of the same value. Keep the two in step. */
  static readonly MD_BREAKPOINT_PX = 768;

  isSidebarExpanded = signal(false);

  /** Escape hatch: force drawer behaviour even on a wide viewport. */
  sidebarForceOverLap = signal(false);

  isMdUp = signal(false);

  isSidebarOverlap = computed(
    () => !this.isMdUp() || this.sidebarForceOverLap()
  );

  constructor() {
    this.initMediaQuery();
  }

  initMediaQuery() {
    const mediaQuery = window.matchMedia(
      `(min-width: ${LayoutService.MD_BREAKPOINT_PX}px)`
    );
    this.setIsMdUp(mediaQuery.matches);
    mediaQuery.addEventListener('change', this.matchMediaChanged);
  }

  matchMediaChanged = (event: MediaQueryListEvent) => {
    this.setIsMdUp(event.matches);
  };

  /**
   * Narrowing past the breakpoint turns the sidebar into a full-viewport overlay.
   * An expanded sidebar carried over from the wide layout (including one restored
   * from the `sidebarPinned` preference) would then cover the page on arrival, so
   * collapse it as we enter overlap mode. Widening is left alone: re-entering the
   * space-taking layout with the sidebar open is the pinned behaviour users expect.
   */
  private setIsMdUp(isMdUp: boolean) {
    const wasOverlap = this.isSidebarOverlap();
    this.isMdUp.set(isMdUp);

    if (!wasOverlap && this.isSidebarOverlap()) {
      this.isSidebarExpanded.set(false);
    }
  }
}
